/*
 * XYNE — Find Your Strip AI chat (polish pass)
 * ---------------------------------------------------------------
 * Chat client for the AI-powered wellness-specialist quiz.
 * - Consumes SSE stream {type:"delta",text:"..."} events from the Worker
 * - Renders assistant text with a smooth chunk-by-chunk fade-in reveal
 * - Shows an INLINE typing indicator as its own thread turn (removed
 *   automatically when the first text chunk arrives)
 * - Reads assistant avatar + name from data attrs on the chat surface
 *   so the theme editor's image_picker can drive it
 * - Parses ```json``` recommendation blocks and animates product cards in
 */

(function () {
  'use strict';

  function init() {
    const root = document.querySelector('[data-rd-chat]');
    if (!root) return;

    const endpoint = root.dataset.endpoint || '';
    const avatarUrl = root.dataset.avatarUrl || '';
    const thread = root.querySelector('[data-rd-thread]');
    const composer = root.querySelector('[data-rd-composer]');
    const input = root.querySelector('[data-rd-input]');
    const sendBtn = root.querySelector('[data-rd-send]');
    const resetBtn = root.querySelector('[data-rd-reset]');
    const errBanner = document.querySelector('[data-rd-error]');
    const catalogEl = document.getElementById('rd-quiz-catalog');
    const catalog = catalogEl ? safeParse(catalogEl.textContent) || {} : {};
    const sidepanel = document.querySelector('[data-rd-sidepanel]');
    const sidepanelCards = document.querySelector('[data-rd-sidepanel-cards]');
    const sidepanelLead = document.querySelector('[data-rd-sidepanel-lead]');

    // Handle → display name aliases for both linkification and fallback
    // detection. Longest aliases first so "Beauty + Collagen" matches before
    // "Beauty" would.
    const HANDLE_ALIASES = [
      { handle: 'probiotic-metabolism-strips', aliases: ['Probiotic + Metabolism Strips', 'Probiotic + Metabolism', 'Probiotic and Metabolism', 'Probiotic Metabolism Strips', 'Probiotic Strips'] },
      { handle: 'appetite-balance-strips',     aliases: ['Appetite Balance & Weight Support Strips', 'Appetite Balance Strips', 'Appetite Balance', 'Appetite Strips'] },
      { handle: 'beauty-collagen-strips',      aliases: ['Beauty + Collagen Strips', 'Beauty + Collagen', 'Beauty and Collagen', 'Beauty Collagen Strips', 'Collagen Strips'] },
      { handle: 'cognitive-relax-strips',      aliases: ['Cognitive Relax Strips', 'Cognitive Relax'] },
      { handle: 'mushroom-focus-strips',       aliases: ['Mushroom Focus Strips', 'Mushroom Focus'] },
      { handle: 'libido-support-strips',       aliases: ['Intimate Vitality Strips', 'Intimate Vitality', 'Vitality Strips', 'Libido Support'] },
      { handle: 'bone-support-strips',         aliases: ['Bone Support Strips', 'Bone Support'] },
      { handle: 'hangover-strips',             aliases: ['Hangover Strips'] },
      { handle: 'energy-strips',               aliases: ['Energy Strips'] },
      { handle: 'iron-strips',                 aliases: ['Iron Strips'] },
      { handle: 'sleep-strips',                aliases: ['Sleep Strips'] },
    ];

    // Seed conversation with the assistant's greeting so it's part of the
    // context sent back on the next turn.
    const greetingBubble = thread.querySelector('[data-rd-turn="assistant"] .rd-quizchat__bubble');
    const greeting = greetingBubble ? greetingBubble.textContent.trim() : '';

    const messages = greeting ? [{ role: 'assistant', content: greeting }] : [];
    let isStreaming = false;
    let currentTypingEl = null;

    // ---------- Textarea auto-grow ----------
    function resizeInput() {
      input.style.height = 'auto';
      input.style.height = Math.min(input.scrollHeight, 160) + 'px';
    }
    input.addEventListener('input', resizeInput);

    // Enter to send, Shift+Enter for newline
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        composer.dispatchEvent(new Event('submit', { cancelable: true }));
      }
      if (e.key === 'Escape') {
        input.blur();
      }
    });

    // Keyboard shortcut — "/" focuses the input from anywhere on the page
    // (skipped when the user is already typing in a form field).
    document.addEventListener('keydown', function (e) {
      if (e.key !== '/') return;
      const tag = (document.activeElement && document.activeElement.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement.isContentEditable) return;
      e.preventDefault();
      input.focus();
    });

    // Screen reader live-region for announcing new assistant messages once
    // the stream completes. Distinct from the thread's aria-live so we can
    // control the exact copy announced (thread contents get noisy).
    let srAnnounce = document.getElementById('rd-quizchat-sr-announce');
    if (!srAnnounce) {
      srAnnounce = document.createElement('div');
      srAnnounce.id = 'rd-quizchat-sr-announce';
      srAnnounce.className = 'rd-visually-hidden';
      srAnnounce.setAttribute('role', 'status');
      srAnnounce.setAttribute('aria-live', 'polite');
      srAnnounce.setAttribute('aria-atomic', 'true');
      document.body.appendChild(srAnnounce);
    }
    function announce(msg) {
      // Clear and set with a tick delay so SR reliably picks up the change
      srAnnounce.textContent = '';
      setTimeout(function () { srAnnounce.textContent = msg; }, 50);
    }

    // ---------- Composer submit ----------
    composer.addEventListener('submit', function (e) {
      e.preventDefault();
      if (isStreaming) return;
      const text = input.value.trim();
      if (!text) return;

      appendUserTurn(text);
      messages.push({ role: 'user', content: text });
      input.value = '';
      resizeInput();
      resetBtn.hidden = false;

      streamAssistant();
    });

    // ---------- Reset ----------
    resetBtn.addEventListener('click', function () {
      if (isStreaming) return;
      thread.innerHTML = '';
      messages.length = 0;
      const li = buildAssistantTurn();
      const bubble = li.querySelector('.rd-quizchat__bubble');
      bubble.innerHTML = '<p>' + escapeHtml(greeting) + '</p>';
      thread.appendChild(li);
      messages.push({ role: 'assistant', content: greeting });
      resetBtn.hidden = true;
      errBanner.hidden = true;
      // Clear the side panel — new conversation, no recommendations yet
      if (sidepanel) sidepanel.hidden = true;
      if (sidepanelCards) sidepanelCards.innerHTML = '';
    });

    // ---------- Turn builders ----------

    function avatarInnerHTML() {
      if (avatarUrl) {
        return '<img src="' + escapeAttr(avatarUrl) + '" alt="" width="36" height="36" loading="lazy" decoding="async">';
      }
      return '<span class="rd-quizchat__avatar-fallback">X</span>';
    }

    function buildAssistantTurn() {
      const li = document.createElement('li');
      li.className = 'rd-quizchat__turn rd-quizchat__turn--assistant';
      li.dataset.rdTurn = 'assistant';
      li.innerHTML =
        '<div class="rd-quizchat__avatar" aria-hidden="true">' + avatarInnerHTML() + '</div>' +
        '<div class="rd-quizchat__bubble" data-rd-assistant-body></div>';
      return li;
    }

    // Turn plain product name mentions into <a> links so the customer can tap
    // through to the PDP even though the shoppable card lives in the side
    // panel. Applied to the finalized (post-stream) bubble HTML.
    function linkifyProductMentions(html) {
      let out = html;
      HANDLE_ALIASES.forEach(function (entry) {
        const p = catalog[entry.handle];
        if (!p) return;
        entry.aliases.forEach(function (alias) {
          // Build a regex that matches the alias as a standalone phrase
          // (with word boundaries at start/end), case-insensitive, without
          // double-linking a mention already wrapped in <a>.
          const escaped = alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const re = new RegExp('(?<!data-rd-product-link[^>]*>)' + escaped + '\\b(?![^<]*<\\/a>)', 'i');
          if (re.test(out)) {
            out = out.replace(re, function (match) {
              return '<a href="' + escapeAttr(p.url) + '" data-rd-product-link="' + escapeAttr(entry.handle) + '">' + match + '</a>';
            });
          }
        });
      });
      return out;
    }

    function appendUserTurn(text) {
      const li = document.createElement('li');
      li.className = 'rd-quizchat__turn rd-quizchat__turn--user';
      li.dataset.rdTurn = 'user';
      li.innerHTML = '<div class="rd-quizchat__bubble">' + linebreaks(escapeHtml(text)) + '</div>';
      thread.appendChild(li);
      scrollToBottom();
    }

    function appendTypingTurn() {
      const li = document.createElement('li');
      li.className = 'rd-quizchat__turn rd-quizchat__turn--assistant rd-quizchat__turn--typing';
      li.dataset.rdTurn = 'typing';
      li.setAttribute('aria-label', 'Assistant is thinking');
      li.innerHTML =
        '<div class="rd-quizchat__avatar" aria-hidden="true">' + avatarInnerHTML() + '</div>' +
        '<div class="rd-quizchat__bubble">' +
          '<span class="rd-quizchat__dots" aria-hidden="true"><span></span><span></span><span></span></span>' +
          '<span class="rd-visually-hidden">Specialist is thinking</span>' +
        '</div>';
      thread.appendChild(li);
      announce('Specialist is thinking');
      scrollToBottom();
      return li;
    }

    function replaceTypingWithAssistant() {
      if (!currentTypingEl) return buildAndAppendAssistantShell();
      const li = buildAssistantTurn();
      const bubble = li.querySelector('.rd-quizchat__bubble');
      bubble.classList.add('rd-quizchat__bubble--streaming');
      thread.replaceChild(li, currentTypingEl);
      currentTypingEl = null;
      scrollToBottom();
      return li;
    }

    function buildAndAppendAssistantShell() {
      const li = buildAssistantTurn();
      const bubble = li.querySelector('.rd-quizchat__bubble');
      bubble.classList.add('rd-quizchat__bubble--streaming');
      thread.appendChild(li);
      scrollToBottom();
      return li;
    }

    // ---------- Rendering ----------

    // Chunked reveal — each SSE delta is wrapped in a span with a fade-in
    // animation, giving the assistant text a smooth flowy feel instead of
    // instant character dumps.
    function appendChunk(bubbleEl, text) {
      if (!text) return;
      const span = document.createElement('span');
      span.className = 'rd-quizchat__chunk';
      span.textContent = text;
      bubbleEl.appendChild(span);
    }

    // After the stream completes, replace all chunk spans + JSON blocks with
    // the plain rendered version (no cursor, no chunks — clean final HTML).
    // Then linkify any product name mentions so customers can tap through.
    function finalizeAssistantBubble(bubbleEl, fullText) {
      bubbleEl.classList.remove('rd-quizchat__bubble--streaming');
      const visible = fullText.replace(/```json[\s\S]*?```/g, '').trim();
      const paragraphs = paragraphize(visible);
      bubbleEl.innerHTML = linkifyProductMentions(paragraphs);
    }

    function paragraphize(text) {
      // Split on blank lines → paragraphs, join lines within a para with <br>
      const paras = text.split(/\n{2,}/);
      return paras
        .map(function (p) { return '<p>' + linebreaks(escapeHtml(p.trim())) + '</p>'; })
        .join('');
    }

    // Render recommendation cards into the SHARED side panel (not inline).
    // Replaces any previous recommendations from earlier turns — the side
    // panel always reflects the latest fit.
    function renderRecommendationCards(jsonBlock) {
      if (!sidepanel || !sidepanelCards) return;
      if (!jsonBlock || !Array.isArray(jsonBlock.recommendations)) return;
      const html = jsonBlock.recommendations
        .map(function (r) { return renderCard(r); })
        .filter(Boolean)
        .join('');
      if (!html) return;
      sidepanelCards.innerHTML = html;
      sidepanel.hidden = false;
      // Update the lead copy under the panel title based on card count
      if (sidepanelLead) {
        const n = jsonBlock.recommendations.length;
        sidepanelLead.textContent = n === 1
          ? 'The single fit from our line, based on what you shared.'
          : n === 2 ? 'Two fits that pair well for your situation.'
          : 'A few fits from our catalog, ordered by relevance.';
      }
    }

    function renderCard(rec) {
      if (!rec || !rec.handle) return '';
      const p = catalog[rec.handle];
      if (!p) return ''; // unknown handle — LLM hallucinated a SKU; skip
      const why = rec.why ? String(rec.why) : '';
      const img = p.image
        ? '<img src="' + escapeAttr(p.image) + '" alt="' + escapeAttr(p.image_alt || p.title) + '" loading="lazy" decoding="async">'
        : '<div class="rd-quizchat__card-imgph" aria-hidden="true"></div>';
      // Optional research links (from the LLM's structured output) — rendered
      // as small chips below the card body. Sanitized to https-only.
      const research = Array.isArray(rec.research)
        ? rec.research
            .filter(function (r) { return r && typeof r.url === 'string' && /^https:\/\//.test(r.url) && r.label; })
            .slice(0, 3)
            .map(function (r) {
              return '<a class="rd-quizchat__card-research-link" href="' + escapeAttr(r.url) +
                     '" target="_blank" rel="noopener noreferrer" aria-label="' + escapeAttr(r.label + ' — opens in a new tab') + '">' +
                     escapeHtml(r.label) +
                     '<svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M14 3h7v7M10 14L21 3M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
                     '</a>';
            })
            .join('')
        : '';
      const researchBlock = research
        ? '<div class="rd-quizchat__card-research" role="group" aria-label="Research sources">' + research + '</div>'
        : '';
      return (
        '<article class="rd-quizchat__card-wrap">' +
          '<a class="rd-quizchat__card" href="' + escapeAttr(p.url) + '" data-rd-card="' + escapeAttr(rec.handle) + '" aria-label="' + escapeAttr(p.title + ' — ' + p.price + ' — view product') + '">' +
            '<div class="rd-quizchat__card-image">' + img + '</div>' +
            '<div class="rd-quizchat__card-body">' +
              '<div class="rd-quizchat__card-title">' + escapeHtml(p.title) + '</div>' +
              (why ? '<div class="rd-quizchat__card-why">' + escapeHtml(why) + '</div>' : '') +
              '<div class="rd-quizchat__card-cta">' +
                '<span class="rd-quizchat__card-price">' + escapeHtml(p.price) + '</span>' +
                '<span class="rd-quizchat__card-shop" aria-hidden="true">Shop →</span>' +
              '</div>' +
            '</div>' +
          '</a>' +
          researchBlock +
        '</article>'
      );
    }

    // ---------- Streaming ----------

    async function streamAssistant() {
      if (!endpoint) {
        showError('Chat endpoint not configured. Set it in the theme editor.');
        return;
      }
      isStreaming = true;
      sendBtn.disabled = true;
      input.disabled = true;
      errBanner.hidden = true;

      // Show the inline typing indicator immediately — feels responsive.
      currentTypingEl = appendTypingTurn();

      let assistantEl = null;
      let bubbleEl = null;
      let acc = '';
      let firstChunk = true;

      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ messages: messages }),
        });

        if (!res.ok) {
          const errJson = await res.json().catch(function () { return {}; });
          throw new Error(errJson.error || ('http ' + res.status));
        }
        if (!res.body) throw new Error('no stream body');

        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = '';

        while (true) {
          const chunk = await reader.read();
          if (chunk.done) break;
          buf += dec.decode(chunk.value, { stream: true });
          const frames = buf.split('\n\n');
          buf = frames.pop() || '';
          for (const frame of frames) {
            const line = frame.split('\n').find(function (l) { return l.indexOf('data:') === 0; });
            if (!line) continue;
            const dataStr = line.slice(5).trim();
            if (!dataStr) continue;
            let obj;
            try { obj = JSON.parse(dataStr); } catch { continue; }

            if (obj.type === 'delta' && typeof obj.text === 'string') {
              // First chunk arrives → swap typing indicator for real bubble
              if (firstChunk) {
                assistantEl = replaceTypingWithAssistant();
                bubbleEl = assistantEl.querySelector('[data-rd-assistant-body]');
                firstChunk = false;
              }
              acc += obj.text;
              // Only append the VISIBLE portion (strip in-progress JSON block)
              const visibleText = extractVisibleChunk(acc, bubbleEl.dataset.rendered || '');
              if (visibleText) {
                bubbleEl.dataset.rendered = (bubbleEl.dataset.rendered || '') + visibleText;
                appendChunk(bubbleEl, visibleText);
                scrollToBottom();
              }
            } else if (obj.type === 'error') {
              throw new Error(obj.error || 'stream error');
            }
          }
        }

        // Stream complete
        if (firstChunk) {
          // Never got a text chunk — assistant returned empty. Bail cleanly.
          if (currentTypingEl) { currentTypingEl.remove(); currentTypingEl = null; }
          throw new Error('empty response');
        }

        finalizeAssistantBubble(bubbleEl, acc);
        let jsonBlock = extractJsonBlock(acc);
        if (!jsonBlock) {
          const fallback = detectProductMentions(acc);
          if (fallback.recommendations.length) jsonBlock = fallback;
        }
        if (jsonBlock) {
          renderRecommendationCards(jsonBlock);
          const n = jsonBlock.recommendations.length;
          announce('Specialist replied. ' + n + ' product ' + (n === 1 ? 'match' : 'matches') + ' shown.');
        } else {
          // Announce the reply text so SR users hear the follow-up question
          announce('Specialist replied. ' + bubbleEl.textContent.slice(0, 240));
        }
        messages.push({ role: 'assistant', content: acc });
      } catch (err) {
        console.error('[quiz] stream error', err);
        if (currentTypingEl) { currentTypingEl.remove(); currentTypingEl = null; }
        if (assistantEl && !acc.trim()) assistantEl.remove();
        showError('Something went wrong on our end. Try again — or browse the full catalog.');
      } finally {
        isStreaming = false;
        sendBtn.disabled = false;
        input.disabled = false;
        input.focus();
      }
    }

    // ---------- Utilities ----------

    // Given the total accumulated text and the portion already rendered as
    // visible chunks, return the NEW visible portion (stripping any JSON
    // fenced block that's mid-stream or completed).
    function extractVisibleChunk(acc, alreadyRendered) {
      // If a ```json marker has started but block isn't closed yet, don't
      // reveal anything past that point (avoids showing raw JSON while it
      // streams). Once the ``` closes, chunks beyond it will render normally.
      const jsonStart = acc.indexOf('```json');
      let visibleAcc;
      if (jsonStart !== -1) {
        const jsonEnd = acc.indexOf('```', jsonStart + 7);
        if (jsonEnd === -1) {
          // block still streaming — only reveal text before the marker
          visibleAcc = acc.slice(0, jsonStart).trimEnd();
        } else {
          // block closed — strip it out
          visibleAcc = (acc.slice(0, jsonStart) + acc.slice(jsonEnd + 3)).trim();
        }
      } else {
        visibleAcc = acc;
      }
      if (visibleAcc.length <= alreadyRendered.length) return '';
      return visibleAcc.slice(alreadyRendered.length);
    }

    function extractJsonBlock(text) {
      // Try fenced ```json ... ``` first
      let m = text.match(/```json\s*([\s\S]*?)```/);
      if (m) { const parsed = safeParse(m[1]); if (parsed) return parsed; }
      // Then untagged fenced ``` ... ``` containing a recommendations key
      m = text.match(/```\s*(\{[\s\S]*?"recommendations"[\s\S]*?\})\s*```/);
      if (m) { const parsed = safeParse(m[1]); if (parsed) return parsed; }
      // Then bare inline JSON object with a recommendations key
      m = text.match(/(\{[^{}]*"recommendations"[\s\S]*?\]\s*[^{}]*\})/);
      if (m) { const parsed = safeParse(m[1]); if (parsed) return parsed; }
      return null;
    }

    // Fallback — scan the assistant's visible text for product mentions and
    // synthesize a recommendations block. Uses HANDLE_ALIASES as the search
    // corpus (case-insensitive). Only fires if the LLM skipped its JSON block.
    function detectProductMentions(text) {
      const lower = text.toLowerCase();
      const hits = [];
      const seen = {};
      HANDLE_ALIASES.forEach(function (entry) {
        if (seen[entry.handle]) return;
        const found = entry.aliases.some(function (a) { return lower.indexOf(a.toLowerCase()) !== -1; });
        if (found && catalog[entry.handle]) {
          hits.push({ handle: entry.handle, why: '' });
          seen[entry.handle] = true;
        }
      });
      return { recommendations: hits.slice(0, 4) };
    }

    function safeParse(s) {
      try { return JSON.parse(s); } catch { return null; }
    }

    function scrollToBottom() {
      requestAnimationFrame(function () {
        thread.scrollTop = thread.scrollHeight;
      });
    }

    function showError(msg) {
      if (!errBanner) return;
      const p = errBanner.querySelector('p');
      if (p && msg) {
        p.textContent = msg + ' ';
        const link = document.createElement('a');
        link.href = '/pages/strips';
        link.textContent = 'Browse all strips.';
        p.appendChild(link);
      }
      errBanner.hidden = false;
    }

    function escapeHtml(s) {
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
    function escapeAttr(s) { return escapeHtml(s); }
    function linebreaks(s) { return s.replace(/\n/g, '<br>'); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
